/**
 * Placeholder text for the brokerage-scoped deal `agent` lookup.
 *
 * The agent roster comes from `broker_agent_access` filtered to the deal's
 * `brokerage_id`, so with no brokerage picked the dropdown is legitimately
 * empty — but all the user sees is a blank Select and AntD's bare "No data"
 * panel, which reads as "this feature is broken" rather than "pick a firm
 * first". The library's WigMetadataForm passes a metadata row's `placeholder`
 * straight through to the Select, so stamping one on the `agent` row says it
 * in the control itself.
 *
 * Returns the SAME array when there is no `agent` lookup row to stamp, so
 * callers can keep this in a memo without churning downstream identities.
 */

/** Shown while no brokerage is selected — the roster cannot be scoped yet. */
export const AGENT_HINT_NO_BROKERAGE = "Select a brokerage first";

/** Shown once a brokerage is selected and the roster is scoped to it. */
export const AGENT_HINT_SCOPED = "Select an agent from this brokerage";

/**
 * Stamp the `agent` lookup row with a brokerage-aware placeholder.
 *
 * `canPickBrokerage: false` says the user has no Brokerage field to act on — a
 * BD.BrokerAgent, whose brokerage is derived from their own broker-access
 * mapping. Telling them to "select a brokerage first" would point at a control
 * they cannot see, so they always get the scoped hint.
 *
 * @param {Array<object>} metadata deals metadata rows
 * @param {number|string|null|undefined} brokerageId the brokerage in scope
 * @param {{ canPickBrokerage?: boolean }} [options]
 * @returns {Array<object>} metadata (same reference when nothing was stamped)
 */
export const withAgentPlaceholder = (metadata, brokerageId, options = {}) => {
  if (!Array.isArray(metadata)) return metadata;
  const { canPickBrokerage = true } = options;
  const missing = brokerageId == null || brokerageId === "";
  const placeholder =
    missing && canPickBrokerage ? AGENT_HINT_NO_BROKERAGE : AGENT_HINT_SCOPED;
  let stamped = false;
  const out = metadata.map((row) => {
    if (row?.field !== "agent" || row?.distinct_values_lookup !== "broker_agents") {
      return row;
    }
    stamped = true;
    return { ...row, placeholder };
  });
  return stamped ? out : metadata;
};

export default withAgentPlaceholder;
